/**
 * Auth Service - Authentication API Layer
 *
 * Centralized authentication operations using ApiClient
 * Replaces direct axios calls in auth/actions.js
 *
 * Benefits:
 * - Type-safe authentication operations
 * - Automatic token injection via ApiClient
 * - Centralized error handling
 * - No direct axios in components/store
 * - Easy to mock for testing
 */

import { AxiosError } from 'axios';
import { apiClient } from './api/client';

// ============================================================================
// TypeScript Interfaces
// ============================================================================

export interface SignupPayload {
  email: string;
  password: string;
  name: string;
  lastName: string;
}

export interface SignupResponse {
  message?: string;
  userId?: string;
}

export interface LoginPayload {
  email: string;
  password: string;
}

export interface LoginResponse {
  userId: string;
  email: string;
  name: string;
  lastName: string;
  role: string;
  accessToken: string;
  idToken: string;
  refreshToken: string;
}

export interface ConfirmForgotPasswordPayload {
  username: string;
  code: string;
  newPassword: string;
}

export interface ChangePasswordPayload {
  oldPassword: string;
  newPassword: string;
}

export interface RefreshTokenResponse {
  AuthenticationResult: {
    AccessToken: string;
    IdToken: string;
    ExpiresIn?: number;
  };
}

// ============================================================================
// AuthService Class
// ============================================================================

export class AuthService {
  /**
   * Create a new user account (signup)
   * Endpoint: POST /users/create_user
   */
  static async signup(payload: SignupPayload): Promise<SignupResponse> {
    try {
      const body = {
        email: payload.email,
        password: payload.password,
        name: payload.name,
        lastName: payload.lastName,
      };

      const response = await apiClient.axios.post('/users/create_user', body);

      console.log('[AuthService] User created in Cognito user pool');
      return response.data || { message: 'User created successfully' };
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string }>;
      console.error('[AuthService] Signup error:', axiosError.response?.data);

      // Extract error message from backend response
      const errorMessage = axiosError.response?.data?.error || 'Failed to create user';
      throw new Error(errorMessage);
    }
  }

  /**
   * Confirm user signup with verification code
   * Endpoint: POST /users/confirm_sign_up
   */
  static async confirmSignUp(username: string, code: string): Promise<void> {
    try {
      const body = { username, code };
      const response = await apiClient.axios.post('/users/confirm_sign_up', body);

      console.log('[AuthService] User confirmed in Cognito user pool');
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string }>;
      console.error('[AuthService] Confirm signup error:', axiosError.response?.data);

      const errorMessage = axiosError.response?.data?.error || 'Failed to confirm signup';
      throw new Error(errorMessage);
    }
  }

  /**
   * Resend confirmation code to user's email
   * Endpoint: POST /users/resend_sign_up_confirmation_code
   */
  static async resendConfirmationCode(username: string): Promise<void> {
    try {
      const body = { username };
      const response = await apiClient.axios.post('/users/resend_sign_up_confirmation_code', body);

      console.log('[AuthService] Confirmation code resent to user');
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string }>;
      console.error('[AuthService] Resend confirmation code error:', axiosError.response?.data);

      const errorMessage = axiosError.response?.data?.error || 'Failed to resend confirmation code';
      throw new Error(errorMessage);
    }
  }

  /**
   * User login with email and password
   * Endpoint: POST /users/sign_in
   *
   * Returns user data and tokens (access, id, refresh)
   * Note: Token storage and expiration handling happens in auth/actions.js
   */
  static async login(email: string, password: string): Promise<LoginResponse> {
    try {
      const body = { email, password };
      const response = await apiClient.axios.post('/users/sign_in', body);

      console.log('[AuthService] User logged in using Cognito');

      // Extract user data from nested response structure
      const userData = response.data.response;

      return {
        userId: userData.userId,
        email: userData.email,
        name: userData.name,
        lastName: userData.lastName,
        role: userData.role,
        accessToken: userData.accessToken,
        idToken: userData.idToken,
        refreshToken: userData.refreshToken,
      };
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string }>;
      console.error('[AuthService] Login error:', axiosError.response?.data);

      const errorMessage = axiosError.response?.data?.error || 'Failed to login';
      throw new Error(errorMessage);
    }
  }

  /**
   * User logout
   * Endpoint: POST /users/sign_out
   *
   * Note: ApiClient auto-injects the accessToken from localStorage
   * Store cleanup happens in auth/actions.js after this call
   */
  static async logout(): Promise<void> {
    try {
      const response = await apiClient.axios.post('/users/sign_out', {});

      console.log('[AuthService] User logged out from Cognito');
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string }>;
      console.error('[AuthService] Logout error:', axiosError.response?.data);

      const errorMessage = axiosError.response?.data?.error || 'Failed to logout';
      throw new Error(errorMessage);
    }
  }

  /**
   * Request password reset (forgot password)
   * Endpoint: POST /users/reset_password_request
   *
   * Sends verification code to user's email
   */
  static async forgotPassword(username: string): Promise<void> {
    try {
      const body = { username };
      const response = await apiClient.axios.post('/users/reset_password_request', body);

      console.log('[AuthService] Reset password request sent successfully');
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string }>;
      console.error('[AuthService] Forgot password error:', axiosError.response?.data);

      const errorMessage = axiosError.response?.data?.error || 'Failed to send reset password request';
      throw new Error(errorMessage);
    }
  }

  /**
   * Confirm password reset with verification code and new password
   * Endpoint: POST /users/reset_password_confirm
   */
  static async confirmForgotPassword(payload: ConfirmForgotPasswordPayload): Promise<void> {
    try {
      const body = {
        username: payload.username,
        code: payload.code,
        newPassword: payload.newPassword,
      };
      const response = await apiClient.axios.post('/users/reset_password_confirm', body);

      console.log('[AuthService] Password reset confirmed successfully');
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string }>;
      console.error('[AuthService] Confirm forgot password error:', axiosError.response?.data);

      const errorMessage = axiosError.response?.data?.error || 'Failed to confirm password reset';
      throw new Error(errorMessage);
    }
  }

  /**
   * Change password for authenticated user
   * Endpoint: POST /users/change_password
   *
   * Note: Requires authentication - ApiClient injects token
   */
  static async changePassword(payload: ChangePasswordPayload): Promise<void> {
    try {
      const body = {
        oldPassword: payload.oldPassword,
        newPassword: payload.newPassword,
      };
      const response = await apiClient.axios.post('/users/change_password', body);

      console.log('[AuthService] Password changed successfully');
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string }>;
      console.error('[AuthService] Change password error:', axiosError.response?.data);

      const errorMessage = axiosError.response?.data?.error || 'Failed to change password';
      throw new Error(errorMessage);
    }
  }

  /**
   * Refresh access token using refresh token
   * Endpoint: POST /users/refresh_access_token
   *
   * Called automatically by ApiClient on 401 responses
   * Returns new access token or null if refresh fails
   *
   * Note: This is used by ApiClient's onUnauthorizedRefresh handler
   */
  static async refreshToken(): Promise<string | null> {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      const userId = localStorage.getItem('userId');

      if (!refreshToken || !userId) {
        console.warn('[AuthService] No refresh token or userId found in localStorage');
        return null;
      }

      const body = { refreshToken, userId };
      const response = await apiClient.axios.post('/users/refresh_access_token', body);

      console.log('[AuthService] Token refreshed successfully');

      const authResult = response.data.response.AuthenticationResult;
      const newAccessToken = authResult.AccessToken;
      const newIdToken = authResult.IdToken;

      // Update localStorage with new tokens
      localStorage.setItem('accessToken', newAccessToken);
      localStorage.setItem('idToken', newIdToken);

      // Update token expiration (1 hour - 10 seconds buffer)
      const expiresIn = 1000 * 60 * 60 - 10000;
      const expirationDate = new Date().getTime() + expiresIn;
      localStorage.setItem('tokenExpiration', String(expirationDate));

      console.log('[AuthService] New token expires in', expiresIn, 'ms');

      return newAccessToken;
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string }>;
      console.error('[AuthService] Token refresh failed:', axiosError.response?.data);

      // On refresh failure, clear tokens to force re-login
      localStorage.removeItem('accessToken');
      localStorage.removeItem('idToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('tokenExpiration');

      return null;
    }
  }

  /**
   * Save Firebase Cloud Messaging (FCM) token to user's database entity
   * Endpoint: POST /users/fcm_token
   *
   * Used by firebase.js to register device for push notifications
   * Note: ApiClient auto-injects the accessToken
   */
  static async saveFcmToken(userUid: string, fcmToken: string): Promise<void> {
    try {
      const body = {
        userUid,
        fcmToken,
      };

      const response = await apiClient.axios.post('/users/fcm_token', body);

      console.log('[AuthService] FCM token saved successfully');
      console.log('[AuthService] Response:', response.status);
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string }>;
      console.error('[AuthService] Save FCM token error:', axiosError.response?.data);

      // Re-throw the original error to maintain backward compatibility
      // firebase.js expects the full error response
      throw axiosError.response?.data || error;
    }
  }

  /**
   * Delete user account
   * Endpoint: POST /users/delete_carenuity_user
   *
   * Permanently deletes the user account from Cognito
   * Note: Store cleanup happens in auth/actions.js after this call
   */
  static async deleteAccount(userId: string): Promise<void> {
    try {
      const body = { userId };
      const response = await apiClient.axios.post('/users/delete_carenuity_user', body);

      console.log('[AuthService] User deleted from Cognito user pool');
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string }>;
      console.error('[AuthService] Delete account error:', axiosError.response?.data);

      const errorMessage = axiosError.response?.data?.error || 'Failed to delete account';
      throw new Error(errorMessage);
    }
  }
}
